import React, { useState, useEffect } from 'react';

const WHATSAPP_URL = 'https://api.whatsapp.com/send/?phone=919624545785';

/**
 * WhatsAppButton — sticky bottom-LEFT floating support button.
 * Hover reveals a rich "Talk to a Specialist" card; click opens WhatsApp.
 */
export default function WhatsAppButton() {
  const [hovered, setHovered] = useState(false);
  const [pastHero, setPastHero] = useState(false);

  // Hide in hero section; show after scrolling past it
  useEffect(() => {
    const onScroll = () => setPastHero(window.scrollY > window.innerHeight * 0.85);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (!pastHero) return null;

  return (
    <>
      {/* Pulse keyframe */}
      <style>{`
        @keyframes wa-pulse {
          0%   { transform: scale(1);    opacity: 0.45; }
          70%  { transform: scale(1.65); opacity: 0;    }
          100% { transform: scale(1.65); opacity: 0;    }
        }
        @keyframes wa-dot-blink {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.35; }
        }
      `}</style>

      <div
        className="fixed bottom-6 left-6 z-[9999] flex flex-row items-end gap-4"
        style={{ pointerEvents: 'none' }}
      >
        {/* ── Trigger Button (bottom-left) ── */}
        <div
          className="relative pointer-events-auto"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          {/* Animated pulse ring */}
          <span
            className="absolute inset-0 rounded-full"
            style={{
              animation: 'wa-pulse 2.4s ease-out infinite',
              background: 'rgba(121,59,251,0.18)',
              pointerEvents: 'none',
            }}
          />

          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Chat with a specialist on WhatsApp"
            className="relative flex items-center justify-center w-[58px] h-[58px] rounded-full"
            style={{
              background: hovered ? '#793bfb' : '#ffffff',
              border: hovered
                ? '2px solid #793bfb'
                : '2px solid rgba(121,59,251,0.3)',
              boxShadow: hovered
                ? '0 0 0 5px rgba(121,59,251,0.12), 0 10px 30px rgba(121,59,251,0.35)'
                : '0 0 0 4px rgba(121,59,251,0.08), 0 6px 20px rgba(0,0,0,0.12)',
              transform: hovered ? 'scale(1.08) translateY(-2px)' : 'scale(1)',
              transition: 'all 0.28s cubic-bezier(0.34,1.56,0.64,1)',
            }}
          >
            {/* Headset / Support icon */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke={hovered ? 'white' : '#793bfb'}
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-6 h-6"
            >
              <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
              <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3z" />
              <path d="M3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
            </svg>
          </a>
        </div>

        {/* ── Hover Card (expands to the right of the button) ── */}
        <div
          className="pointer-events-auto mb-1"
          style={{
            transform: hovered
              ? 'translateX(0) scale(1)'
              : 'translateX(-12px) scale(0.96)',
            opacity: hovered ? 1 : 0,
            transition: 'all 0.35s cubic-bezier(0.34,1.56,0.64,1)',
            transformOrigin: 'bottom left',
            pointerEvents: hovered ? 'auto' : 'none',
          }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <div
            className="w-[300px] rounded-2xl p-5 flex flex-col gap-4"
            style={{
              background: '#ffffff',
              border: '1.5px solid rgba(121,59,251,0.45)',
              boxShadow:
                '0 16px 48px rgba(121,59,251,0.12), 0 4px 16px rgba(0,0,0,0.08)',
            }}
          >
            {/* Header row */}
            <div className="flex items-center justify-between">
              <span
                className="font-mono text-[10px] font-bold tracking-[0.2em] uppercase"
                style={{ color: '#793bfb' }}
              >
                [ Live Connection ]
              </span>
              {/* Live dot */}
              <span
                className="w-2.5 h-2.5 rounded-full bg-[#793bfb]"
                style={{ animation: 'wa-dot-blink 1.8s ease-in-out infinite' }}
              />
            </div>

            {/* Title */}
            <div>
              <h4
                className="font-display text-sm font-black uppercase tracking-wide leading-snug"
                style={{ color: '#1a1030' }}
              >
                Talk to a Specialist
              </h4>
              <p className="font-sans text-[11px] mt-1 leading-relaxed" style={{ color: '#6b7280' }}>
                Get direct engineering guidance &amp; quotation for custom camera
                configurations:
              </p>
            </div>

            {/* Bullet list */}
            <ul className="flex flex-col gap-1.5">
              {[
                'Camera Stabilization (Stabilizers, Gimbals)',
                'Virtual Production (LED volumes, tracking)',
                'Cinema Lenses (Anamorphic &amp; Prime)',
                'Communication Systems (Eartec Wireless)',
              ].map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-2 font-sans text-[11px] leading-snug"
                  style={{ color: '#374151' }}
                >
                  <span
                    className="mt-[5px] w-1.5 h-1.5 rounded-full flex-shrink-0"
                    style={{ background: '#793bfb' }}
                  />
                  <span dangerouslySetInnerHTML={{ __html: item }} />
                </li>
              ))}
            </ul>

            {/* CTA Button */}
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl font-mono text-[11px] font-bold uppercase tracking-widest transition-all duration-200 hover:brightness-110 active:scale-[0.98]"
              style={{
                background: 'linear-gradient(90deg, #793bfb 0%, #793bfb 100%)',
                color: '#ffffff',
                boxShadow: '0 4px 16px rgba(246,174,45,0.35)',
              }}
            >
              {/* Chat bubble icon */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-3.5 h-3.5"
              >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              Open WhatsApp Chat
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
